import React from 'react';
import { Card, Typography, Row, Col, Statistic, Progress, Spin, Alert } from 'antd';
import {
  UserOutlined,
  ShoppingCartOutlined,
  DollarOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from '@ant-design/icons';
import { useQuery } from 'react-query';
import api from '../services/api';

const { Title } = Typography;

const Analytics = () => {
  // Fetch dashboard statistics
  const {
    data: dashboardData,
    isLoading,
    error,
  } = useQuery(
    // Its own cache key: the dashboard caches the same endpoint under a
    // different shape, and sharing the key left this page reading fields that
    // were never there, so every figure fell back to zero.
    'analytics-dashboard-stats',
    async () => {
      try {
        const response = await api.get('/admin/dashboard-stats');
        return response.data?.data || response.data || {};
      } catch (error) {
        console.error('Failed to fetch dashboard stats:', error);
        if (error.response?.status === 401) {
          window.location.href = '/login';
        }
        throw error;
      }
    },
    {
      refetchInterval: 300000, // Refresh every 5 minutes
      staleTime: 2 * 60 * 1000, // 2 minutes
    },
  );

  // Calculate additional metrics
  const calculateGrowthRate = () => {
    // This would ideally come from the API, but for now we'll calculate a mock growth rate
    const totalOrders = dashboardData?.total_orders || 0;

    // Mock growth calculation - in real implementation this would compare with previous period
    if (totalOrders > 10) return 15.3;
    if (totalOrders > 5) return 8.7;
    return -2.1;
  };

  const calculateSalesPerformance = () => {
    const totalOrders = dashboardData?.total_orders || 0;
    const targetOrders = 100; // Mock target
    return Math.min((totalOrders / targetOrders) * 100, 100);
  };

  const calculateCustomerSatisfaction = () => {
    return dashboardData?.customer_satisfaction?.rate ?? 0;
  };

  const growthRate = calculateGrowthRate();
  const salesPerformance = calculateSalesPerformance();
  const customerSatisfaction = calculateCustomerSatisfaction();

  if (error) {
    return (
      <Alert
        message="Error Loading Analytics"
        description="Failed to load analytics data. Please try again later."
        type="error"
        showIcon
        style={{ marginBottom: 24 }}
      />
    );
  }

  return (
    <div>
      <Title level={2}>Analytics Dashboard</Title>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Total Users"
                  value={dashboardData?.total_users || 0}
                  prefix={<UserOutlined />}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Total Orders"
                  value={dashboardData?.total_orders || 0}
                  prefix={<ShoppingCartOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Revenue"
                  value={dashboardData?.total_revenue || 0}
                  prefix={<DollarOutlined />}
                  precision={2}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Growth Rate"
                  value={growthRate}
                  prefix={growthRate >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                  suffix="%"
                  precision={1}
                  valueStyle={{ color: growthRate >= 0 ? '#3f8600' : '#cf1322' }}
                />
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} md={12}>
              <Card title="Sales Performance">
                <Progress
                  percent={salesPerformance}
                  status="active"
                  strokeColor={{
                    '0%': '#108ee9',
                    '100%': '#87d068',
                  }}
                />
                <p style={{ marginTop: 8 }}>
                  {salesPerformance.toFixed(1)}% of monthly target achieved (
                  {dashboardData?.total_orders || 0} orders)
                </p>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card title="Customer Satisfaction">
                <Progress
                  percent={customerSatisfaction}
                  status="active"
                  strokeColor={{
                    '0%': '#f50',
                    '100%': '#22c55e',
                  }}
                />
                <p style={{ marginTop: 8 }}>{customerSatisfaction}% customer satisfaction rate</p>
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} md={8}>
              <Card>
                <Statistic
                  title="Products/Services"
                  value={dashboardData?.total_products || 0}
                  prefix={<ShoppingCartOutlined />}
                  valueStyle={{ color: '#13c2c2' }}
                />
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card>
                <Statistic
                  title="Avg Order Value"
                  value={
                    dashboardData?.total_orders && dashboardData?.total_orders > 0
                      ? (dashboardData?.total_revenue / dashboardData?.total_orders).toFixed(2)
                      : 0
                  }
                  prefix={<DollarOutlined />}
                  precision={2}
                  valueStyle={{ color: '#fa8c16' }}
                />
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card>
                <Statistic
                  title="Orders per User"
                  value={
                    dashboardData?.total_users && dashboardData?.total_users > 0
                      ? (dashboardData?.total_orders / dashboardData?.total_users).toFixed(2)
                      : 0
                  }
                  prefix={<UserOutlined />}
                  precision={2}
                  valueStyle={{ color: '#eb2f96' }}
                />
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default Analytics;
